import React, { useCallback, useRef } from 'react';

import {
    GoogleMap,
    Marker,
} from '@react-google-maps/api';

import { getLogger } from 'wanderu-logger';

import StationDetailPopover from './StationDetailPopover';

import styles from './StationMapCore.scss';

const logger = getLogger('station_map');

import type {
    MapMode, Position, Trip, SelectedStation,
} from './StationMapCore';

interface StationMapPaneProps {
    center: Position;
    label: string;
    labelMuted: string;
    onPinClick: (trip: Trip, side: MapMode, mapRef: React.RefObject<google.maps.Map | null>) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    selectedStation: SelectedStation | undefined;
    side: MapMode;
    trips: Trip[];
    isStationFiltered: boolean;
    tripsAtStation: number;
    onFilter: () => void;
    onClosePopover: () => void;
}

const fillContainerStyle = {
    height: '100%',
    width: '100%',
};

export function fitMapToStations(
    map: google.maps.Map,
    trips: Trip[],
    positionKey: 'departLatLong' | 'arriveLatLong',
): void {
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;
    trips.forEach((trip) => {
        const pos = trip[positionKey];
        if (pos) {
            bounds.extend(pos);
            hasPoints = true;
        }
    });
    if (!hasPoints) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    if (ne.lat() === sw.lat() && ne.lng() === sw.lng()) {
        map.setCenter(ne);
    } else {
        map.fitBounds(bounds, 40);
    }
}

function StationMapPane({
    center,
    label,
    labelMuted,
    onPinClick,
    onZoomIn,
    onZoomOut,
    selectedStation,
    side,
    trips,
    isStationFiltered,
    tripsAtStation,
    onFilter,
    onClosePopover,
}: StationMapPaneProps) {
    const mapRef = useRef<google.maps.Map | null>(null);
    const prevZoomRef = useRef<number>(11);
    const loadedRef = useRef(false);
    const settledRef = useRef(false);

    const positionKey = side === 'depart'
        ? 'departLatLong'
        : 'arriveLatLong';

    const handlePinClick = useCallback(
        (trip: Trip) => onPinClick(trip, side, mapRef),
        [onPinClick, side],
    );

    const handleZoomChanged = useCallback(() => {
        const map = mapRef.current;
        if (!map || !settledRef.current) return;
        const newZoom = map.getZoom();
        if (newZoom == null) return;
        if (newZoom > prevZoomRef.current) onZoomIn();
        if (newZoom < prevZoomRef.current) onZoomOut();
        prevZoomRef.current = newZoom;
    }, [onZoomIn, onZoomOut]);

    const handleIdle = useCallback(() => {
        if (settledRef.current || !loadedRef.current) return;
        settledRef.current = true;
        const zoom = mapRef.current?.getZoom();
        if (zoom != null) prevZoomRef.current = zoom;
    }, []);

    return (
        <div className={styles.mapPane}>
            <div className={styles.paneLabel}>
                <span className={styles.paneLabelMuted}>
                    {labelMuted}
                </span>
                {' '}
                {label}
            </div>
            <GoogleMap
                center={center}
                mapContainerStyle={fillContainerStyle}
                onIdle={handleIdle}
                onLoad={(map) => {
                    mapRef.current = map;
                    fitMapToStations(map, trips, positionKey);
                    loadedRef.current = true;
                    logger.event('map_load', side);
                }}
                onZoomChanged={handleZoomChanged}
                options={{
                    disableDefaultUI: true,
                    scaleControl: true,
                    zoomControl: true,
                }}
                zoom={11}
            >
                {trips?.map((trip) => {
                    const position = trip[positionKey];
                    return position && (
                        <Marker
                            key={`${side[0]}-${trip.tripId}`}
                            onClick={() => handlePinClick(trip)}
                            position={position}
                        />
                    );
                })}
            </GoogleMap>
            {selectedStation && selectedStation.side === side && (
                <div className={styles.popoverSlot}>
                    <StationDetailPopover
                        direction={side}
                        isFiltered={isStationFiltered}
                        onClose={onClosePopover}
                        onFilter={onFilter}
                        stationId={selectedStation.id}
                        stationName={selectedStation.name}
                        tripsAtStation={tripsAtStation}
                    />
                </div>
            )}
        </div>
    );
}

export default React.memo(StationMapPane);
